import { apiService } from '@/shared/services/apiServices';
import serviceRequestEndpoints from './serviceRequestEndpoints';

export interface AcknowledgeServiceRequestPayload {
  status: 'accepted' | 'rejected';
  reason?: string;
}

export interface AcknowledgeServiceRequestData {
  serviceRequestId: string;
  status: string;
}

export interface AcknowledgeServiceRequestResult {
  error: boolean;
  data?: AcknowledgeServiceRequestData;
  status?: number;
  isNetworkError?: boolean;
  /** True when the backend returned 403 — the technician's privilege was revoked. */
  permissionDenied?: boolean;
  /** True when the backend returned 409 — the acknowledgement window has expired. */
  windowExpired?: boolean;
}

/**
 * Sends the technician's accept or reject decision to the backend.
 *
 * @remarks
 * HTTP 403 → sets `permissionDenied: true` (privilege revoked while on page).
 * HTTP 409 → sets `windowExpired: true` (SR no longer in ASSIGNED status).
 * The hook maps these flags to user-facing error messages.
 *
 * @param serviceRequestId - Internal UUID from the route path.
 * @param payload - `{ status, reason? }` — reason required when status = "rejected".
 */
export const acknowledgeServiceRequestApiService = async (
  serviceRequestId: string,
  payload: AcknowledgeServiceRequestPayload
): Promise<AcknowledgeServiceRequestResult> => {
  const response = await apiService.patchAPI(
    serviceRequestEndpoints.ACKNOWLEDGE_SERVICE_REQUEST(serviceRequestId),
    payload
  );

  if (response.status === 403) {
    return { error: true, status: response.status, permissionDenied: true };
  }

  if (response.status === 409) {
    return { error: true, status: response.status, windowExpired: true };
  }

  const result: AcknowledgeServiceRequestResult = { error: response.error };
  if (response.data !== undefined) {
    result.data = response.data as AcknowledgeServiceRequestData;
  }
  if (response.status !== undefined) {
    result.status = response.status;
  }
  if (response.isNetworkError !== undefined) {
    result.isNetworkError = response.isNetworkError;
  }
  return result;
};
